<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router/composables'
import { IEventEdit } from '@/enitites/event'
// eslint-disable-next-line import/no-cycle
import EventTotalSection from '@/pages/CreateEventProcessingPage/ui/CreateEventTotal/EventTotalSection.vue'
import EventEditActionButtons from '@/pages/CreateEventProcessingPage/ui/layout/EventEditActionButtons.vue'
import EventGeneralInfo from '@/pages/CreateEventProcessingPage/ui/CreateEventTotal/EventGeneralInfo.vue'
import EventDateTime from '@/pages/CreateEventProcessingPage/ui/CreateEventTotal/EventDateTime.vue'
import EventBattles from '@/pages/CreateEventProcessingPage/ui/CreateEventTotal/EventBattles.vue'
import EventDisplayMode from '@/pages/CreateEventProcessingPage/ui/CreateEventTotal/EventDisplayMode.vue'
import EventPaymentSystem from '@/pages/CreateEventProcessingPage/ui/CreateEventTotal/EventPaymentSystem.vue'
import EventOwnPaymentMethod from '@/pages/CreateEventProcessingPage/ui/CreateEventTotal/EventOwnPaymentMethod.vue'
import EventBankAccount from '@/pages/CreateEventProcessingPage/ui/CreateEventTotal/EventBankAccount.vue'
import EventTicketsDiscounts from '@/pages/CreateEventProcessingPage/ui/CreateEventTotal/EventTicketsDiscounts.vue'
import EventRefundPolicy from '@/pages/CreateEventProcessingPage/ui/CreateEventTotal/EventRefundPolicy.vue'
import EventGuestList from '@/pages/CreateEventProcessingPage/ui/CreateEventTotal/EventGuestList.vue'
import { useEventPublishMutation, useEventDeleteMutation } from '@/enitites/event/api/queries'
import { ModalWrapper } from '@/shared/ui/modals'

const props = defineProps<{
  eventData: IEventEdit
}>()

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'next'): void
  (e: 'cancel'): void
}>()

const { mutate, isPending, error } = useEventPublishMutation()
const { mutate: deleteEvent, isPending: isDeletePending } = useEventDeleteMutation()
const router = useRouter()

const isShowDeleteModal = ref(false)

const handleNext = () => {
  if (props.eventData.status === 'published') {
    emit('next')
    return
  }

  mutate(props.eventData.id, {
    onSuccess: () => {
      emit('next')
    },
  })
}

const handleDeleteEvent = () => {
  if (isDeletePending.value) return

  deleteEvent(props.eventData.id, {
    onSuccess: () => {
      router.push({ name: 'EventsPage' })
    },
  })
}
</script>

<template>
  <div class="create-event-total pb-10">
    <EventTotalSection
      step="info"
      title="General Information"
      :event-id="eventData.id"
    >
      <EventGeneralInfo :event-data="eventData" />
    </EventTotalSection>

    <EventTotalSection
      step="date"
      title="Date and time *"
      :event-id="eventData.id"
    >
      <EventDateTime :event-data="eventData" />
    </EventTotalSection>

    <EventTotalSection
      step="battles"
      :event-id="eventData.id"
      title="Battles"
    >
      <EventBattles :event-data="eventData" />
    </EventTotalSection>

    <EventTotalSection
      step="display-mode"
      :event-id="eventData.id"
      title="Display Mode"
    >
      <EventDisplayMode :event-data="eventData" />
    </EventTotalSection>

    <EventTotalSection
      step="payment-mode"
      :event-id="eventData.id"
      title="Break Konnect Payment System"
    >
      <EventPaymentSystem :event-data="eventData" />
    </EventTotalSection>

    <template v-if="!eventData.paymentSystem?.isEnabled">
      <EventTotalSection
        step="own-payment-mode"
        :event-id="eventData.id"
        title="Own Payment Method"
      >
        <EventOwnPaymentMethod :event-data="eventData" />
      </EventTotalSection>
    </template>

    <template v-if="eventData.paymentSystem?.isEnabled">
      <EventTotalSection
        step="bank-account"
        :event-id="eventData.id"
        title="Bank Account Information"
      >
        <EventBankAccount :event-data="eventData" />
      </EventTotalSection>

      <EventTotalSection
        step="tickets"
        :event-id="eventData.id"
        title="Tickets and Discounts"
      >
        <EventTicketsDiscounts :event-data="eventData" />
      </EventTotalSection>

      <EventTotalSection
        step="refund-policy"
        :event-id="eventData.id"
        title="Refund Policy"
      >
        <EventRefundPolicy :event-data="eventData" />
      </EventTotalSection>

      <EventTotalSection
        step="guests"
        :event-id="eventData.id"
        title="Guest List"
      >
        <EventGuestList :event-data="eventData" />
      </EventTotalSection>
    </template>

    <EventEditActionButtons
      :is-loading="isPending"
      :error="error?.message"
      :disabled="false"
      :next-button-text="eventData.status === 'published' ? 'Save' : 'Publish Event'"
      :hide-cancel-button="eventData.status === 'published'"
      @back="$emit('back')"
      @next="handleNext"
      @cancel="$emit('cancel')"
    />

    <div class="btn-delete">
      <v-btn
        v-if="eventData.status === 'published'"
        blok
        class="btn-delete"
        color="red"
        height="48"
        text
        @click="isShowDeleteModal = true"
      >
        Delete Event
      </v-btn>
    </div>

    <ModalWrapper
      :is-show="isShowDeleteModal"
      @update:isShow="isShowDeleteModal = $event"
    >
      <v-card tile>
        <div class="col-8 py-10 mx-auto">
          <v-container>
            <v-row>
              <v-card-title class="delete__title">Delete Event</v-card-title>
            </v-row>
            <v-card-text class="pa-1">
              Are you sure you want to delete this event? This action cannot be undone.
            </v-card-text>
          </v-container>

          <v-card-actions
            :class="{ 'flex-column': $vuetify.breakpoint.xsOnly }"
            class="d-flex"
          >
            <v-btn
              :loading="isDeletePending"
              class="px-10 mx-3 my-2"
              color="#f21849"
              height="48"
              outlined
              tile
              @click="handleDeleteEvent"
            >
              Delete event
            </v-btn>
            <v-btn
              :disabled="isDeletePending"
              class="px-10 mx-3 my-2"
              color="#000000"
              height="48"
              outlined
              tile
              @click="isShowDeleteModal = false"
            >
              Cancel
            </v-btn>
          </v-card-actions>
        </div>
      </v-card>
    </ModalWrapper>
  </div>
</template>

<style lang="scss" scoped>
.create-event-total {
  margin-top: 90px;
}

.btn-delete {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}
</style>
